/**
 * Tooltip — meta-component over the shadcn Tooltip primitive.
 *
 * Wraps Provider / Root / Trigger / Content into a single props-driven API.
 * Pass any element as `children` (the trigger) and the tooltip text via `content`.
 *
 *   <Tooltip content="Full date: 20 Apr 2026, 14:32">
 *     <span>2 hours ago</span>
 *   </Tooltip>
 *
 *   <Tooltip content="Delete this item" side="right" variant="default">
 *     <Button icon={Trash2} variant="ghost" size="sm" />
 *   </Tooltip>
 */

import type { ReactNode } from 'react';
import {
  Tooltip as ShadTooltip,
  TooltipContent as ShadTooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const VARIANT_CLASSES = {
  /** Standard dark tooltip. */
  default: '',
  /** Muted / secondary tooltip for supplementary info. */
  muted: 'bg-muted text-muted-foreground [&_[data-slot=tooltip-arrow]]:bg-muted [&_[data-slot=tooltip-arrow]]:fill-muted',
  /** Warning-toned tooltip. */
  warning: 'bg-yellow-600 text-white [&_[data-slot=tooltip-arrow]]:bg-yellow-600 [&_[data-slot=tooltip-arrow]]:fill-yellow-600',
  /** Destructive-toned tooltip. */
  destructive: 'bg-destructive text-destructive-foreground [&_[data-slot=tooltip-arrow]]:bg-destructive [&_[data-slot=tooltip-arrow]]:fill-destructive',
} as const;

export type TooltipVariant = keyof typeof VARIANT_CLASSES;

export interface TooltipProps {
  /** Tooltip body — text or rich content shown on hover/focus. */
  content: ReactNode;
  /** The trigger element. Must accept a ref (native elements or components using forwardRef). */
  children: ReactNode;
  /** Tooltip placement relative to the trigger. @default "top" */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Visual variant. @default "default" */
  variant?: TooltipVariant;
  /** Delay in ms before the tooltip appears. @default 150 */
  delayDuration?: number;
  /** Extra classes merged onto the tooltip content container. */
  className?: string;
}

export function Tooltip({
  content,
  children,
  side = 'top',
  variant = 'default',
  delayDuration = 150,
  className,
}: TooltipProps) {
  return (
    <TooltipProvider delayDuration={delayDuration}>
      <ShadTooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <ShadTooltipContent
          side={side}
          className={cn('max-w-xs text-xs', VARIANT_CLASSES[variant], className)}
        >
          {content}
        </ShadTooltipContent>
      </ShadTooltip>
    </TooltipProvider>
  );
}
